import { Anchor, Button, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { Link } from 'wouter';
import { CollapsibleCard } from '@/components/CollapsibleCard';
import type { ContactImportNote, EmailHolder } from '@/models/contacts';

export function ImportNotesCard({
  notes,
  onAddEmail,
}: {
  notes: ContactImportNote[];
  onAddEmail: () => void;
}) {
  if (notes.length === 0) return null;
  return (
    <CollapsibleCard title="From Monday">
      <Stack gap="xs">
        {notes.map((n) => (
          <Text
            key={n.kind === 'email_taken' ? n.email : `${n.column}-${n.name}`}
            fz="sm"
          >
            {n.kind === 'email_taken' ? (
              <EmailTaken
                email={n.email}
                holder={n.holder}
                onAddEmail={onAddEmail}
              />
            ) : (
              <>
                {n.column ?? 'Owner'} in Monday: <b>{n.name ?? n.email}</b>
                {n.name && n.email ? ` (${n.email})` : ''} - not a BO user.
              </>
            )}
          </Text>
        ))}
      </Stack>
    </CollapsibleCard>
  );
}

function EmailTaken({
  email,
  holder,
  onAddEmail,
}: {
  email: string;
  holder: EmailHolder | null;
  onAddEmail: () => void;
}) {
  const addEmail = (
    <Button size="compact-xs" variant="light" ml={6} onClick={onAddEmail}>
      Add email
    </Button>
  );
  if (!holder) {
    return (
      <>
        The email from Monday <b>{email}</b> was taken at import, but the email
        is free now - add it in Identity info.{addEmail}
      </>
    );
  }
  const label = holderLabel(holder);
  if (holder.type === 'lead') {
    return (
      <>
        The email from Monday <b>{email}</b> belongs to lead{' '}
        {holder.visible ? (
          <Anchor component={Link} to={`~/leads/${holder.id}`} fz="sm">
            {label}
          </Anchor>
        ) : (
          label
        )}
        . An offer cannot be made on a lead: add the email to this contact in
        Identity info and sell here.{addEmail}
      </>
    );
  }
  if (!holder.visible) {
    return (
      <>
        The email from Monday <b>{email}</b> belongs to contact {label}
        {holder.accountName ? ` (account ${holder.accountName})` : ''}, which
        you do not see - ask a user who can assign contacts to add you to it,
        then sell on that contact.
      </>
    );
  }
  return (
    <>
      <b>{email}</b> belongs to contact{' '}
      <Anchor component={Link} to={`~/contacts/${holder.id}`} fz="sm">
        {label}
      </Anchor>
      {holder.accountName ? ` (account ${holder.accountName})` : ''} - sell on
      that contact.
    </>
  );
}

function holderLabel(holder: EmailHolder): ReactNode {
  return holder.name ? `${holder.id} ${holder.name}` : String(holder.id);
}
